import type { AssetDescription } from '@play-co/timestep-core';
import {
  ImageScaleView,
  MultiplyFilter,
  QuickViewPool,
  View,
  animate,
} from '@play-co/timestep-core/ui';
import { spineLoader } from '@play-co/timestep-spine';
import { DOWNSCALE_FACTOR } from 'src/conf/scale';
import SpineAnimationView from 'src/game/components/shared/SpineAnimationView';
import { dreamDriftWearPet } from 'src/game/dreamDrift/dreamDrift.costumes';
import type { PositionType } from 'src/game/minigame/match2-timestep/lib/defs/types';
import { getPet } from 'src/game/pet';
import { createUIPet } from 'src/game/pet/pet.utils';
import { petAnimationNames } from 'src/replicant/pet/PetAssets';
import sounds from 'src/lib/sounds';
import type { GameScene } from '../../minigame/match2-timestep/game/GameScene';
import { mapToViewPosition } from '../../minigame/match2-timestep/game/util/mapTools';
import { waitFor } from '../../minigame/match2-timestep/lib/util/waitFor';

/** Spine export used by the Prism Painter opening presentation. */
export const prismPainterPowerAnimationAsset = {
  loadMethod: spineLoader,
  url: 'assets/ui/dreamDrift/powerPainter/spine.Dreamdrift.painter',
};

/** Image used by the color-matched particles emitted from painted blocks. */
export const prismPainterParticleImage =
  'assets/ui/dreamDrift/powerPainter/particle.png';

/** All assets required by the Prism Painter opening presentation. */
export const prismPainterPowerAnimationAssets: (
  | string
  | AssetDescription<unknown>
)[] = [prismPainterPowerAnimationAsset, prismPainterParticleImage];

/** Concrete board color supported by the Prism Painter presentation. */
export type PrismPainterPaintColor =
  | 'red'
  | 'green'
  | 'blue'
  | 'yellow'
  | 'purple';

/** Exact tint applied to particles for each resulting basic-tile color. */
const PRISM_PAINTER_PARTICLE_TINTS: Record<PrismPainterPaintColor, string> = {
  red: '#FF1414FF',
  green: '#66E403FF',
  blue: '#00D2FFFF',
  yellow: '#FCD430FF',
  purple: '#CE37FFFF',
};

/** Duration used to move the paintbrush to each selected board cell. */
const PRISM_PAINTER_MOVE_DURATION_MS = 300;

/** Duration the paintbrush animation plays over each selected board cell. */
const PRISM_PAINTER_PAINT_DURATION_MS = 300;

/** Horizontal distance used to place the paintbrush safely outside the board. */
const PRISM_PAINTER_OFFSCREEN_DISTANCE = 600;

/** Scale authored on the pet placeholder bone in the Spine setup pose. */
const PRISM_PAINTER_PET_SETUP_SCALE = 0.5;

/** Number of reusable particles emitted during each block's paint stroke. */
const PRISM_PAINTER_PARTICLE_COUNT = 5;

/** Callback applying the board conversion for one reached paint target. */
type PrismPainterPaintStep = (targetIndex: number) => void;

/** Callback emitting the visual effect while one paint stroke plays. */
type PrismPainterPaintEffect = (viewPosition: PositionType) => void;

/**
 * Scatter one pooled particle burst from a painted block.
 *
 * @param particlePool Reusable particle views owned by this presentation.
 * @param viewPosition Center of the newly painted tile.
 * @returns Nothing after every particle fades and returns to the pool.
 */
async function playPrismPainterParticleBurst(
  particlePool: QuickViewPool<ImageScaleView>,
  viewPosition: PositionType,
): Promise<void> {
  // Spread every emission evenly across the complete paint stroke.
  const particleInterval =
    PRISM_PAINTER_PAINT_DURATION_MS / PRISM_PAINTER_PARTICLE_COUNT;
  const particleAnimations: Promise<void>[] = [];

  // Give each delayed particle an independent offset and outward trajectory.
  for (
    let particleIndex = 0;
    particleIndex < PRISM_PAINTER_PARTICLE_COUNT;
    particleIndex++
  ) {
    const particle = particlePool.obtainView();
    const angle = Math.random() * Math.PI * 2;
    const startDistance = Math.random() * 20;
    const travelDistance = 40 + Math.random() * 20;
    const startScale = 1.5 + Math.random() * 0.3;
    const startX = (viewPosition.x ?? 0) + Math.cos(angle) * startDistance;
    const startY = (viewPosition.y ?? 0) + Math.sin(angle) * startDistance;

    // Reset every property that may have changed during its previous use.
    particle.updateOpts({
      x: startX,
      y: startY,
      r: Math.random() * Math.PI * 2,
      scale: startScale,
      visible: true,
    });

    // Drift outward while shrinking and fading, then recycle the view.
    const particleAnimation = animate(particle)
      .then(
        {
          x: startX + Math.cos(angle) * travelDistance,
          y: startY + Math.sin(angle) * travelDistance,
          r: particle.style.r + (Math.random() - 0.5) * Math.PI,
          scale: 0,
        },
        600,
        animate.easeOut,
      )
      .toPromise()
      .then(() => particlePool.releaseView(particle));
    particleAnimations.push(particleAnimation);

    // Delay the next emission so particles cover the full paint duration.
    await waitFor(particleInterval);
  }

  // Keep the presentation alive until the complete burst has been recycled.
  await Promise.all(particleAnimations);
}

/**
 * Shake the complete Dream Drift scene during one paint stroke.
 *
 * @param sceneRoot Root view containing the puzzle and Painter presentation.
 * @returns Nothing after the short shake restores the responsive scene origin.
 */
async function shakePrismPainterScene(sceneRoot: View): Promise<void> {
  // Preserve the responsive scene position so repeated shakes cannot drift it.
  const originX = sceneRoot.style.x ?? 0;
  const originY = sceneRoot.style.y ?? 0;

  // Alternate subtle offsets before settling within the 300ms paint stroke.
  await animate(sceneRoot)
    .now({ x: originX - 3, y: originY + 1 }, 35, animate.linear)
    .then({ x: originX + 2, y: originY - 1 }, 40, animate.linear)
    .then({ x: originX - 2, y: originY + 1 }, 40, animate.linear)
    .then({ x: originX + 1, y: originY }, 30, animate.linear)
    .then({ x: originX, y: originY }, 35, animate.easeOut)
    .toPromise();
}

/**
 * Resolve one paint target in the same view coordinate space as puzzle blocks.
 *
 * @param game Active Dream Drift Match-2 runtime.
 * @param position Board cell touched by the travelling paintbrush.
 * @returns Center of the cell's tile view in the puzzle layer coordinate space.
 */
function getPrismPainterViewPosition(
  game: GameScene,
  position: PositionType,
): PositionType {
  // Use the spawned tile view so the brush shares block positioning exactly.
  const tileView =
    game.sessionEntity.c.map.getCellAt(position)?.tile?.c.view2d.view;
  if (tileView) {
    return {
      x: tileView.x + tileView.width / 2,
      y: tileView.y + tileView.height / 2,
    };
  }

  // Fall back to the standard conversion for an unexpectedly missing tile view.
  const viewPosition = mapToViewPosition(position, true);
  return { x: viewPosition.x, y: viewPosition.y };
}

/**
 * Move the paused paintbrush to one destination using a short upward hop.
 *
 * @param paintbrushMovementContainer Parent view controlling horizontal movement.
 * @param paintbrushSpine Foreground brush presentation being moved.
 * @param destination Final view position centered over a board cell.
 * @returns Nothing after the brush lands at the destination.
 */
async function movePrismPainterBrush(
  paintbrushMovementContainer: View,
  paintbrushSpine: SpineAnimationView,
  destination: PositionType,
): Promise<void> {
  // Resolve concrete coordinates and the halfway duration for the destination.
  const destinationX = destination.x ?? 0;
  const destinationY = destination.y ?? 0;
  const halfDuration = PRISM_PAINTER_MOVE_DURATION_MS / 2;

  // Move horizontally at a constant speed for the complete hop duration.
  const horizontalMovement = animate(paintbrushMovementContainer)
    .then({ x: destinationX }, PRISM_PAINTER_MOVE_DURATION_MS, animate.linear)
    .toPromise();

  // Rise and fall independently while the brush moves horizontally.
  const verticalMovement = animate(paintbrushSpine)
    .then({ y: destinationY - 100 }, halfDuration, animate.easeOut)
    .then({ y: destinationY }, halfDuration, animate.easeIn)
    .toPromise();

  // Wait until both axes reach the destination before painting the block.
  await Promise.all([horizontalMovement, verticalMovement]);
}

/**
 * Move and play the travelling paintbrush over every selected board position.
 *
 * @param paintbrushMovementContainer Parent view controlling horizontal movement.
 * @param paintbrushSpine Paused foreground brush presentation.
 * @param targetViewPositions Ordered centers of the selected puzzle tiles.
 * @param onPaintStep Callback applying one board conversion by target index.
 * @param onPaintEffect Callback emitting one effect while the tile is painted.
 * @returns Nothing after the brush finishes beyond the board edge.
 */
async function runPrismPainterBrushSequence(
  paintbrushMovementContainer: View,
  paintbrushSpine: SpineAnimationView,
  targetViewPositions: PositionType[],
  onPaintStep: PrismPainterPaintStep,
  onPaintEffect: PrismPainterPaintEffect,
): Promise<void> {
  // Target-free presentations should never reveal a misplaced brush.
  if (!targetViewPositions.length) return;

  // Start the travelling brush paused before its first offscreen entrance.
  paintbrushSpine.show();
  paintbrushSpine.start({
    id: 'paintbrush_loop',
    loop: true,
    mix: 0,
    speed: 0,
  });
  paintbrushSpine.state.timeScale = 0;

  // Move, paint, and pause once for every selected board target.
  for (
    let targetIndex = 0;
    targetIndex < targetViewPositions.length;
    targetIndex++
  ) {
    const viewPosition = targetViewPositions[targetIndex];
    await movePrismPainterBrush(
      paintbrushMovementContainer,
      paintbrushSpine,
      viewPosition,
    );

    // Play the brush stroke and its staggered particles over the selected tile.
    paintbrushSpine.state.timeScale = 1;
    sounds.sfx.play('dreamDriftPaintDrop');
    onPaintEffect(viewPosition);
    await waitFor(PRISM_PAINTER_PAINT_DURATION_MS);

    // Complete the conversion before pausing for the next movement.
    onPaintStep(targetIndex);
    paintbrushSpine.state.timeScale = 0;
  }

  // Move the paused brush beyond the board after the final conversion.
  const lastViewPosition = targetViewPositions[targetViewPositions.length - 1];
  const rightmostTargetX = Math.max(
    ...targetViewPositions.map((position) => position.x ?? 0),
  );
  await movePrismPainterBrush(paintbrushMovementContainer, paintbrushSpine, {
    x: rightmostTargetX + PRISM_PAINTER_OFFSCREEN_DISTANCE,
    y: lastViewPosition.y,
  });
}

/**
 * Play the supplied Prism Painter presentation over selected board targets.
 *
 * @param game Active Dream Drift Match-2 runtime.
 * @param targetPositions Ordered board positions repainted by the power.
 * @param paintColor Resulting block color used to tint each particle burst.
 * @param onPaintStep Callback applying one board conversion by target index.
 * @returns Nothing after the complete presentation finishes.
 */
export async function runPrismPainterPowerAnimation(
  game: GameScene,
  targetPositions: PositionType[],
  paintColor: PrismPainterPaintColor | undefined,
  onPaintStep: PrismPainterPaintStep,
): Promise<void> {
  // Load the supplied Spine export before changing the visible puzzle scene.
  await spineLoader(prismPainterPowerAnimationAsset.url);

  // Resolve the standard Match-2 scene hierarchy from the game stage.
  const puzzleContainer = game.stage.view.getSuperview();
  const sceneRoot = puzzleContainer?.getSuperview();
  if (!puzzleContainer || !sceneRoot) return;

  // Dim the scene behind the bright puzzle grid during the hero presentation.
  const dim = new View({
    superview: sceneRoot,
    width: sceneRoot.style.width,
    height: sceneRoot.style.height,
    backgroundColor: '#000000',
    opacity: 0,
    canHandleEvents: false,
    zIndex: (puzzleContainer.style.zIndex ?? 0) - 0.5,
  });

  // Center the native 1080x1920 composition over the responsive scene.
  const presentationContainer = new View({
    superview: sceneRoot,
    x: sceneRoot.style.width / 2,
    y: sceneRoot.style.height / 2,
    scale: DOWNSCALE_FACTOR,
    canHandleEvents: false,
    zIndex: 100000,
  });

  // Keep the authored pet effects above the player pet placeholder.
  const presentationSpine = new SpineAnimationView({
    superview: presentationContainer,
    spinePath: prismPainterPowerAnimationAsset.url,
    x: 0,
    y: 0,
    zIndex: 1,
    visible: false,
  });

  // Instantiate the player's pet beneath the supplied effects composition.
  const species = getPet().species ?? 'dog';
  const pet = createUIPet('normal', species);
  pet.setAnimation(petAnimationNames[species].angry);
  dreamDriftWearPet(pet, 'prismPainter');
  pet.updateOpts({
    superview: presentationContainer,
    anchorY: 300,
    offsetY: -300,
    scale: 2.4,
    zIndex: 0,
  });

  /** Keep the pet below the authored placeholder for the complete animation. */
  const updatePetWithSpine = (): void => {
    // The painter export uses the shared Dream Drift placeholder bone contract.
    const bone = presentationSpine.skeleton.findBone('placeholder_bear');
    if (!bone) return;

    pet.updateOpts({
      x: bone.worldX,
      y: bone.worldY + 50,
      r: (bone.getWorldRotationX() * Math.PI) / 180,
      scaleX: bone.getWorldScaleX() * PRISM_PAINTER_PET_SETUP_SCALE,
      scaleY: bone.getWorldScaleY() * PRISM_PAINTER_PET_SETUP_SCALE,
    });
  };
  presentationContainer.setTick(updatePetWithSpine);

  // Resolve every selected cell before the brush begins changing the board.
  const targetViewPositions = targetPositions.map((position) =>
    getPrismPainterViewPosition(game, position),
  );
  const firstViewPosition = targetViewPositions[0];
  const paintbrushMovementContainer = new View({
    superview: game.view2d.layers.foreground.view,
    x: firstViewPosition ? -PRISM_PAINTER_OFFSCREEN_DISTANCE : 0,
    y: 0,
    canHandleEvents: false,
    zIndex: 100000,
  });
  const paintbrushSpine = new SpineAnimationView({
    superview: paintbrushMovementContainer,
    spinePath: prismPainterPowerAnimationAsset.url,
    x: 0,
    y: firstViewPosition?.y ?? 0,
    scale: DOWNSCALE_FACTOR * 0.7,
    zIndex: 0,
    visible: false,
  });

  // Match the authored brush attachments to the resulting board color.
  if (paintColor) {
    paintbrushSpine.skeleton.setSkinByName(paintColor);
    paintbrushSpine.skeleton.setSlotsToSetupPose();
  }

  // Keep particles behind the brush while remaining above the puzzle board.
  const particleContainer = new View({
    superview: game.view2d.layers.foreground.view,
    canHandleEvents: false,
    zIndex: 99999,
  });
  let particlePool: QuickViewPool<ImageScaleView> | undefined;
  if (paintColor) {
    const particleTint = new MultiplyFilter(
      PRISM_PAINTER_PARTICLE_TINTS[paintColor],
    );
    particlePool = new QuickViewPool(() => {
      const particle = new ImageScaleView({
        superview: particleContainer,
        image: prismPainterParticleImage,
        width: 96,
        height: 96,
        centerOnOrigin: true,
        scaleMethod: 'contain',
        canHandleEvents: false,
        visible: false,
      });
      particle.setFilter(particleTint);
      return particle;
    });
    // Preallocate two bursts because the longer lifetimes overlap each other.
    particlePool.initPool(PRISM_PAINTER_PARTICLE_COUNT * 2);
  }
  const paintEffectAnimations: Promise<void>[] = [];

  /** Start the synchronized shake and particles for one active paint stroke. */
  const playPaintEffect: PrismPainterPaintEffect = (viewPosition): void => {
    // Shake the complete scene while the brush paints the reached block.
    paintEffectAnimations.push(shakePrismPainterScene(sceneRoot));

    // Target-free presentations do not have a particle color or pool.
    if (!particlePool) return;

    // Let particles overlap the brush's following movement before recycling.
    paintEffectAnimations.push(
      playPrismPainterParticleBurst(particlePool, viewPosition),
    );
  };

  // Dim the scene before revealing the opening pet animation.
  await animate(dim).then({ opacity: 0.5 }, 300, animate.linear).toPromise();

  // Play the export's authored entrance before holding the pet on screen.
  presentationSpine.show();
  const petEnter = presentationSpine.start({ id: 'pet_init', mix: 0 });
  await petEnter.complete;

  // Keep the pet idling while the paintbrush travels between selected blocks.
  presentationSpine.start({ id: 'pet_idle', loop: true, mix: 0 });

  // Move and paint every selected tile while the pet remains in its idle pose.
  await runPrismPainterBrushSequence(
    paintbrushMovementContainer,
    paintbrushSpine,
    targetViewPositions,
    onPaintStep,
    playPaintEffect,
  );

  // Remove the board brush before playing the authored pet exit.
  paintbrushSpine.state.clearTracks();
  paintbrushSpine.hide();
  const petExit = presentationSpine.start({ id: 'pet_exit', mix: 0 });
  await petExit.complete;

  // Restore the scene after the complete presentation finishes.
  await animate(dim).then({ opacity: 0 }, 300, animate.linear).toPromise();

  // Let longer-lived paint effects finish without delaying the pet exit sequence.
  await Promise.all(paintEffectAnimations);

  // Remove all temporary animation state after the presentation finishes.
  presentationContainer.setTick(null);
  presentationSpine.state.clearTracks();
  presentationSpine.state.clearListeners();
  paintbrushSpine.state.clearTracks();
  paintbrushSpine.state.clearListeners();
  paintbrushMovementContainer.removeFromSuperview();
  particleContainer.removeFromSuperview();
  presentationContainer.removeFromSuperview();
  dim.removeFromSuperview();
}
